import React, { createContext, useContext, useState } from 'react';
import { Gateway } from '../../../../types';
import { useNavigate } from 'react-router-dom';
import { setActiveGateway } from '../../../../app/store/gateway.slice';
import { useAppDispatch } from '../../../../app/store';

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

type GatewayCardContextType = {
    gateway: Gateway;
    loading: boolean;
    menu: {
        isOpen: boolean;
        open: (event: React.MouseEvent<HTMLButtonElement>) => void;
        close: (route?: undefined | string) => void;
        anchorEl: null | HTMLElement;
    };
    dialogs: {
        cancel: {
            isOpen: boolean;
            open: () => void;
            close: () => void;
        };
    };
};

type GatewayCardContextProviderType = {
    value: Optional<
        Pick<GatewayCardContextType, 'gateway' | 'loading'>,
        'gateway' | 'loading'
    >;
    children: JSX.Element;
};

export const GatewayCardContext = createContext<GatewayCardContextType>(
    {} as GatewayCardContextType,
);

export const GatewayCardProvider: React.FC<GatewayCardContextProviderType> = ({
    children,
    value,
}) => {
    const [cancellationDialogIsOpen, setCancellationDialog] =
        useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const dispatch = useAppDispatch();

    const navigate = useNavigate();
    const open = Boolean(anchorEl);

    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = (route: undefined | string = undefined) => {
        if (route && value?.gateway) {
            dispatch(setActiveGateway(value.gateway));
            navigate(`${value?.gateway?.website}/settings/${route}`);
        }

        setAnchorEl(null);
    };

    const handleOpenCancellationDialog = (): void => {
        handleCloseMenu();
        setCancellationDialog(true);
    };

    const handleCloseCancellationDialog = (): void => {
        setCancellationDialog(false);
    };


    const initialValues: GatewayCardContextType = {
        gateway: value?.gateway || ({} as Gateway),
        loading:
            value?.loading ||
            (value?.gateway?.permissions &&
                Object.keys(value?.gateway?.permissions)?.length === 0) ||
            false,
        menu: {
            isOpen: open,
            anchorEl: anchorEl,
            open: handleOpenMenu,
            close: handleCloseMenu,
        },
        dialogs: {
            cancel: {
                isOpen: cancellationDialogIsOpen,
                open: handleOpenCancellationDialog,
                close: handleCloseCancellationDialog,
            },
        },
    };

    return (
        <GatewayCardContext.Provider value={initialValues}>
            {children}
        </GatewayCardContext.Provider>
    );
};

export const useGatewayCardContext = (): GatewayCardContextType => {
    return useContext<GatewayCardContextType>(GatewayCardContext);
};
